import { useTRPC } from '@/utils/trpc';
import { needsCcdaRender, withRenderedCcda } from '@/utils/ccdaDocuments';
import type { WithId } from '@medplum/core';
import type { DocumentReference } from '@medplum/fhirtypes';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Enrich encounter DocumentReferences so C-CDA (`application/xml`) documents render inline instead of
 * as a download link. For each document that needs it (see {@link needsCcdaRender}) it calls the
 * server-side `documentReference.renderHtml` transform and folds the resulting HTML back in via
 * {@link withRenderedCcda}; documents that already render inline pass through untouched.
 *
 * Rendering is progressive enhancement — while a transform is in flight (or if it fails) the document
 * keeps its existing presentation, so the encounter view never blocks on it.
 */
export const useEnrichCcdaDocuments = (documents: DocumentReference[] | undefined): DocumentReference[] | undefined => {
  const trpc = useTRPC();

  const renderable = useMemo<WithId<DocumentReference>[]>(
    () => (documents ?? []).filter((doc): doc is WithId<DocumentReference> => Boolean(doc.id) && needsCcdaRender(doc)),
    [documents],
  );

  const renderQueries = useQueries({
    queries: renderable.map((doc) =>
      trpc.patient.documentReference.renderHtml.queryOptions({ documentReferenceId: doc.id }),
    ),
  });

  const htmlByDocId = useMemo(() => {
    const map: Record<string, string> = {};
    renderable.forEach((doc, index) => {
      const html = renderQueries[index]?.data?.html;
      if (html) map[doc.id] = html;
    });
    return map;
  }, [renderable, renderQueries]);

  return useMemo(() => {
    if (!documents) return documents;
    return documents.map((doc) => {
      const html = doc.id ? htmlByDocId[doc.id] : undefined;
      return html ? withRenderedCcda(doc, html) : doc;
    });
  }, [documents, htmlByDocId]);
};
